import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  MapPin,
  Clock,
  Maximize2,
  CheckCircle2,
  Building2,
  Layers,
  ChevronLeft,
  ChevronRight,
  X,
  Sparkles,
  Share2,
  Calendar,
  Ruler,
  UserCheck,
  Check
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { FinalCTA } from "@/components/site/FinalCTA";
import { Reveal } from "@/components/site/Reveal";
import { BeforeAfterSlider } from "@/components/site/BeforeAfterSlider";
import { ALL_PROJECTS, getProjectBySlug } from "@/lib/projectsData";

export default function ProjectDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeRoomIndex, setActiveRoomIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  const project = getProjectBySlug(slug) || ALL_PROJECTS[0];

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 500], [0, 100]);

  // Find index for next/prev project navigation
  const currentIndex = ALL_PROJECTS.findIndex((p) => p.id === project.id);
  const nextProject = ALL_PROJECTS[(currentIndex + 1) % ALL_PROJECTS.length];
  const prevProject = ALL_PROJECTS[(currentIndex - 1 + ALL_PROJECTS.length) % ALL_PROJECTS.length];

  useEffect(() => {
    document.title = `${project.title} | Projects — Bloc & Brush Dubai`;
    window.scrollTo(0, 0);
  }, [slug, project]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: project.title,
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openLightbox = (index) => {
    setActiveImageIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="bg-white text-[#1C1C1C] min-h-screen">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* STICKY TOP BREADCRUMB / ACTION BAR */}
      <div className="pt-24 bg-[#13294B] text-white border-b border-white/10 relative z-30">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl py-3 flex flex-wrap items-center justify-between gap-4 text-xs font-medium">
          <div className="flex items-center gap-2 text-white/70">
            <Link to="/projects" className="hover:text-white transition">Projects</Link>
            <span>/</span>
            <span className="text-white/50">{project.city}</span>
            <span>/</span>
            <span className="text-white font-bold">{project.title}</span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-full border border-white/15 transition text-xs"
            >
              {copied ? <Check size={13} className="text-[#25D366]" /> : <Share2 size={13} />}
              {copied ? "Link Copied!" : "Share Project"}
            </button>
            <Link
              to="/projects/showcase"
              className="inline-flex items-center gap-1.5 bg-white text-[#13294B] px-4 py-1.5 rounded-full font-bold transition text-xs shadow-sm"
            >
              <ArrowLeft size={13} /> Back to Showcase
            </Link>
          </div>
        </div>
      </div>

      {/* HERO SECTION */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden bg-[#0E1F3A] text-white">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-[120%] object-cover opacity-40"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1F3A] via-[#0E1F3A]/70 to-transparent" />

        <div className="relative container mx-auto px-6 lg:px-12 max-w-7xl py-20 z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center gap-2 mb-6"
          >
            <span className="bg-[#25D366] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              {project.bhk}
            </span>
            <span className="bg-white/10 backdrop-blur text-white/90 text-xs font-semibold px-3 py-1 rounded-full border border-white/15 flex items-center gap-1">
              <MapPin size={12} /> {project.area} · {project.city}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-4xl md:text-5xl lg:text-[68px] font-extrabold leading-[1.02] tracking-tight max-w-4xl"
          >
            {project.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 text-base md:text-xl text-white/80 max-w-2xl leading-relaxed font-normal"
          >
            {project.subtitle || project.description}
          </motion.p>

          {/* PROJECT KEY METRICS BAR */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 border-t border-white/15 max-w-4xl"
          >
            <div>
              <div className="flex items-center gap-1.5 text-white/50 text-xs uppercase tracking-wider font-semibold">
                <Ruler size={14} /> Area Size
              </div>
              <div className="font-display text-xl md:text-2xl font-bold mt-1">{project.areaSqft || "1,450 sq ft"}</div>
            </div>

            <div>
              <div className="flex items-center gap-1.5 text-white/50 text-xs uppercase tracking-wider font-semibold">
                <Clock size={14} /> Delivery Speed
              </div>
              <div className="font-display text-xl md:text-2xl font-bold mt-1 text-[#25D366]">{project.turnkeyTimeline || "16 Days"}</div>
            </div>

            <div>
              <div className="flex items-center gap-1.5 text-white/50 text-xs uppercase tracking-wider font-semibold">
                <UserCheck size={14} /> Client Category
              </div>
              <div className="font-display text-xl md:text-2xl font-bold mt-1">{project.clientType || "Holiday Home"}</div>
            </div>

            <div>
              <div className="flex items-center gap-1.5 text-white/50 text-xs uppercase tracking-wider font-semibold">
                <Calendar size={14} /> Delivered
              </div>
              <div className="font-display text-xl md:text-2xl font-bold mt-1">{project.completionYear || "2025"}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 1: ASYMMETRIC GALLERY MOSAIC (UNIQUE NON-GRID VISUAL GALLERY) */}
      <section className="py-24 bg-[#F5F5F5] border-b border-black/5">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-[#707070] font-semibold">
                  Architectural Photography Gallery
                </span>
                <h2 className="font-display mt-2 text-3xl md:text-4xl lg:text-[46px] font-extrabold text-[#1C1C1C]">
                  Curated Image Stage.
                </h2>
              </div>

              <div className="flex items-center gap-2 text-xs text-[#707070]">
                <Sparkles size={15} className="text-[#13294B]" />
                Click any image to open full-screen interactive lightbox
              </div>
            </div>
          </Reveal>

          {/* ASYMMETRIC GALLERY MOSAIC */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            {/* FEATURE SPOTLIGHT (8 COLS) */}
            <div
              onClick={() => openLightbox(0)}
              className="lg:col-span-8 relative rounded-3xl overflow-hidden min-h-[420px] lg:min-h-[580px] bg-gray-900 shadow-[0_20px_50px_rgba(19,41,75,0.1)] group cursor-pointer border border-black/5"
            >
              <img
                src={project.gallery?.[0] || project.image}
                alt={`${project.title} gallery spotlight`}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 opacity-80 group-hover:opacity-60 transition" />
              
              <div className="absolute bottom-6 left-6 right-6 text-white flex items-end justify-between">
                <div>
                  <span className="bg-[#13294B] text-white text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                    Primary Stage View
                  </span>
                  <h3 className="font-display text-xl lg:text-2xl font-bold mt-2">{project.title}</h3>
                </div>
                <div className="bg-white/90 text-[#13294B] p-3 rounded-full shadow-lg group-hover:scale-110 transition">
                  <Maximize2 size={18} />
                </div>
              </div>
            </div>

            {/* SIDE STACK (4 COLS) */}
            <div className="lg:col-span-4 flex flex-col gap-6">
              {(project.gallery?.slice(1, 3) || [project.image, project.image]).map((imgUrl, i) => (
                <div
                  key={i}
                  onClick={() => openLightbox(i + 1)}
                  className="relative flex-1 rounded-3xl overflow-hidden min-h-[260px] bg-gray-900 shadow-md group cursor-pointer border border-black/5"
                >
                  <img
                    src={imgUrl}
                    alt={`${project.title} view ${i + 2}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <div className="absolute bottom-4 right-4 bg-white/90 text-[#13294B] p-2.5 rounded-full shadow transition group-hover:scale-110">
                    <Maximize2 size={15} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* HORIZONTAL DRAGGABLE GALLERY REEL FOR REMAINING IMAGES */}
          {project.gallery && project.gallery.length > 3 && (
            <div className="mt-8 pt-8 border-t border-black/5">
              <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#707070] mb-4">
                Additional Room Views ({project.gallery.length} Photos)
              </h4>
              <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar">
                {project.gallery.map((imgUrl, idx) => (
                  <div
                    key={idx}
                    onClick={() => openLightbox(idx)}
                    className="shrink-0 w-52 aspect-[4/3] rounded-2xl overflow-hidden relative cursor-pointer border border-black/10 group shadow-sm hover:shadow-md transition"
                  >
                    <img
                      src={imgUrl}
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition" />
                    <div className="absolute bottom-2 left-2 text-[10px] bg-black/60 text-white px-2 py-0.5 rounded font-medium">
                      0{idx + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* SECTION 2: INTERACTIVE BEFORE & AFTER TRANSFORMATION */}
      <section className="py-24 bg-[#0E1F3A] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <span className="text-xs uppercase tracking-[0.25em] text-[#25D366] font-semibold">
                Transformation Process
              </span>
              <h2 className="font-display mt-3 text-3xl md:text-4xl lg:text-[46px] font-extrabold leading-tight">
                Bare Property Shell To Luxury Residence.
              </h2>
              <p className="mt-6 text-base text-white/75 leading-relaxed">
                Use the interactive slider to compare the property shell prior to fit-out against Bloc & Brush's completed turnkey interior.
              </p>

              <div className="mt-8 space-y-4">
                {project.scope?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm text-white/90">
                    <CheckCircle2 size={18} className="text-[#25D366] shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <BeforeAfterSlider
                beforeImage={project.beforeImage || "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1600&q=85"}
                afterImage={project.afterImage || project.image}
                beforeLabel="Before Fit-Out"
                afterLabel="Bloc & Brush Turnkey Finish"
                className="aspect-[4/3] w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: ROOM-BY-ROOM NARRATIVE & MATERIAL SPECS */}
      {project.rooms && project.rooms.length > 0 && (
        <section className="py-24 bg-white border-b border-black/5">
          <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
            <Reveal>
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                <div>
                  <span className="text-xs uppercase tracking-[0.25em] text-[#707070] font-semibold">
                    Spatial Design & Material Palette
                  </span>
                  <h2 className="font-display mt-2 text-3xl md:text-4xl lg:text-[46px] font-extrabold text-[#1C1C1C]">
                    Room-by-Room Tour.
                  </h2>
                </div>
              </div>
            </Reveal>

            {/* ROOM TABS */}
            <div className="flex items-center gap-3 overflow-x-auto pb-4 hide-scrollbar">
              {project.rooms.map((room, idx) => (
                <button
                  key={room.title}
                  onClick={() => setActiveRoomIndex(idx)}
                  className={`px-6 py-3 rounded-2xl text-sm font-bold whitespace-nowrap transition-all border ${
                    activeRoomIndex === idx
                      ? "bg-[#13294B] text-white border-[#13294B] shadow-lg"
                      : "bg-[#F5F5F5] text-[#707070] hover:bg-black/5"
                  }`}
                >
                  {room.title}
                </button>
              ))}
            </div>

            {/* ACTIVE ROOM DISPLAY */}
            {project.rooms[activeRoomIndex] && (
              <div className="mt-8 bg-[#F5F5F5] rounded-3xl p-8 lg:p-12 border border-black/5 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-6 space-y-4">
                  <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#13294B]">
                    Zone 0{activeRoomIndex + 1}
                  </span>
                  <h3 className="font-display text-2xl lg:text-3xl font-extrabold text-[#1C1C1C]">
                    {project.rooms[activeRoomIndex].title}
                  </h3>
                  <p className="text-base text-[#1C1C1C]/75 leading-relaxed">
                    {project.rooms[activeRoomIndex].desc}
                  </p>

                  {/* MATERIALS SWATCHES */}
                  {project.materials && (
                    <div className="pt-6 border-t border-black/10">
                      <h4 className="text-xs uppercase tracking-[0.2em] text-[#707070] font-semibold mb-3">
                        Curated Material Swatches
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {project.materials.map((mat, i) => (
                          <span key={i} className="bg-white border border-black/10 text-[#13294B] text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                            {mat}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="lg:col-span-6 aspect-[4/3] rounded-2xl overflow-hidden shadow-lg bg-gray-900">
                  <img
                    src={project.rooms[activeRoomIndex].image}
                    alt={project.rooms[activeRoomIndex].title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* NEXT / PREVIOUS PROJECT NAVIGATOR */}
      <section className="py-20 bg-[#F5F5F5] border-t border-black/5">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <div className="text-xs uppercase tracking-[0.25em] text-[#707070] font-semibold text-center mb-8">
            Explore More Turnkey Projects
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* PREVIOUS PROJECT */}
            <Link
              to={`/projects/${prevProject.slug}`}
              className="bg-white p-8 rounded-3xl border border-black/5 hover:border-[#13294B]/30 hover:shadow-xl transition-all duration-300 flex items-center justify-between group"
            >
              <div>
                <span className="text-xs uppercase tracking-wider text-[#707070] flex items-center gap-1">
                  <ChevronLeft size={14} /> Previous Residence
                </span>
                <h4 className="font-display text-xl font-bold text-[#1C1C1C] mt-2 group-hover:text-[#13294B] transition">
                  {prevProject.title}
                </h4>
                <p className="text-xs text-[#707070] mt-1">{prevProject.area} · {prevProject.city}</p>
              </div>
              <img src={prevProject.image} alt={prevProject.title} className="w-20 h-20 rounded-2xl object-cover shrink-0 ml-4" />
            </Link>

            {/* NEXT PROJECT */}
            <Link
              to={`/projects/${nextProject.slug}`}
              className="bg-white p-8 rounded-3xl border border-black/5 hover:border-[#13294B]/30 hover:shadow-xl transition-all duration-300 flex items-center justify-between group"
            >
              <img src={nextProject.image} alt={nextProject.title} className="w-20 h-20 rounded-2xl object-cover shrink-0 mr-4" />
              <div className="text-right">
                <span className="text-xs uppercase tracking-wider text-[#707070] flex items-center gap-1 justify-end">
                  Next Residence <ChevronRight size={14} />
                </span>
                <h4 className="font-display text-xl font-bold text-[#1C1C1C] mt-2 group-hover:text-[#13294B] transition">
                  {nextProject.title}
                </h4>
                <p className="text-xs text-[#707070] mt-1">{nextProject.area} · {nextProject.city}</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* FULLSCREEN INTERACTIVE LIGHTBOX CAROUSEL */}
      <AnimatePresence>
        {lightboxOpen && (
          <div className="fixed inset-0 z-[110] bg-black/95 flex flex-col justify-between p-4 md:p-8 backdrop-blur-md">
            {/* TOP CONTROLS */}
            <div className="flex items-center justify-between text-white z-20">
              <div className="text-xs font-semibold uppercase tracking-widest text-white/70">
                {project.title} — Image {activeImageIndex + 1} of {project.gallery?.length || 1}
              </div>
              <button
                onClick={() => setLightboxOpen(false)}
                className="bg-white/10 hover:bg-white/30 text-white p-3 rounded-full transition"
                aria-label="Close Lightbox"
              >
                <X size={22} />
              </button>
            </div>

            {/* MAIN LIGHTBOX IMAGE */}
            <div className="relative flex-1 flex items-center justify-center my-4 overflow-hidden">
              <button
                onClick={() =>
                  setActiveImageIndex((prev) =>
                    prev === 0 ? (project.gallery?.length || 1) - 1 : prev - 1
                  )
                }
                className="absolute left-4 z-20 bg-white/10 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur transition"
                aria-label="Previous Image"
              >
                <ChevronLeft size={24} />
              </button>

              <motion.img
                key={activeImageIndex}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.3 }}
                src={project.gallery?.[activeImageIndex] || project.image}
                alt={`Full preview ${activeImageIndex + 1}`}
                className="max-h-[80vh] max-w-[90vw] object-contain rounded-2xl shadow-2xl"
              />

              <button
                onClick={() =>
                  setActiveImageIndex((prev) =>
                    prev === (project.gallery?.length || 1) - 1 ? 0 : prev + 1
                  )
                }
                className="absolute right-4 z-20 bg-white/10 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur transition"
                aria-label="Next Image"
              >
                <ChevronRight size={24} />
              </button>
            </div>

            {/* THUMBNAIL STRIP */}
            {project.gallery && (
              <div className="flex justify-center gap-3 overflow-x-auto py-2 z-20 hide-scrollbar">
                {project.gallery.map((imgUrl, i) => (
                  <img
                    key={i}
                    src={imgUrl}
                    alt={`Thumbnail ${i + 1}`}
                    onClick={() => setActiveImageIndex(i)}
                    className={`h-14 w-20 object-cover rounded-xl cursor-pointer border-2 transition ${
                      activeImageIndex === i ? "border-[#25D366] scale-105" : "border-transparent opacity-50 hover:opacity-100"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </AnimatePresence>

      <FinalCTA onQuoteClick={() => setQuoteOpen(true)} />
      <Footer onQuoteClick={() => setQuoteOpen(true)} />
      <WhatsAppFloat />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
